import type { EnumAppointmentReminderStatus } from "@bbj-types/action/appointmentReminder";
import type { IUser } from "@bbj-types/user";
import type { ObjectId } from "@bbj-types/utils";
import type { IWorkshop, IWorkshopStatus } from "@bbj-types/workshop";
import { useSuspense } from "@root/api/tools/axios";
import { useCallback } from "react";
import { workshopsAxios, workshopsAxiosInstance } from "./axios";

export interface IGetWorkshopsBody {
  limit: number;
  skip: number;
  sessionType?: string[];
  animators?: ObjectId[];
  status?: IWorkshopStatus[];
  date?: {
    gte?: Date;
    lte?: Date;
  };
  startTime?: {
    gte?: Date;
    lte?: Date;
  };
  endTime?: {
    gte?: Date;
    lte?: Date;
  };
  places?: string[];
  allUserOrganisations?: boolean;
  sort?: Record<string, 1 | -1>;
}

export type TGetWorkshops = Pick<
  IWorkshop,
  | "_id"
  | "sessionType"
  | "date"
  | "startTime"
  | "endTime"
  | "address"
  | "organisation"
  | "organizer"
  | "convocationTimes"
> & {
  mainAnimator?: Pick<IUser, "_id" | "firstName" | "lastName">;
  otherAnimators: Pick<IUser, "_id" | "firstName" | "lastName">[];
  status: IWorkshopStatus;
  presenceCount: number;
  invitedCount: number;
  deniedCount: number;
  acceptedCount: number;
  participantCount: number;
  hasEventBeenSent: boolean;
  lastInvitationDate?: Date;
  highlighted: boolean;
  appointmentReminderStatus?: EnumAppointmentReminderStatus;
};

export interface IGetWorkshopsResponse {
  data: TGetWorkshops[];
  count: number;
  skip: number;
  limit: number;
  total: number;
}

export const useGetWorkshops = () => {
  const [state, execute, cancel] = workshopsAxios<IGetWorkshopsResponse, IGetWorkshopsBody>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (query: IGetWorkshopsBody) => {
      return execute({ params: query });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useSupenseGetAllWorkshops = (params: IGetWorkshopsBody) => {
  return useSuspense(
    () =>
      workshopsAxiosInstance.get<IGetWorkshopsResponse>("/", {
        withCredentials: true,
        params,
      }),
    [params]
  );
};
